<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

export type Option = { id: string; name: string };

export interface FetchPageArgs {
  page: number;
  pageSize: number;
  search: string;
}

export interface FetchPageResult {
  items: Option[];
  hasMore: boolean;
  total?: number;
}

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    modelValue: Option | null;
    label: string;
    disabled?: boolean;
    loading?: boolean;
    pageSize?: number;
    placeholder?: string;
    prependIcon?: string;
    fetchPage: (args: FetchPageArgs) => Promise<FetchPageResult>;
  }>(),
  {
    disabled: false,
    pageSize: 20,
    placeholder: "",
    prependIcon: "mdi-magnify",
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", v: Option | null): void;
}>();

const internal = computed({
  get: () => props.modelValue,
  set: (v: Option | null) => emit("update:modelValue", v),
});

const items = ref<Option[]>([]);
const search = ref("");
const menuOpen = ref(false);

const page = ref(1);
const hasMore = ref(true);

const isLoading = ref(false);
const isLoadingMore = ref(false);

let debounceTimer: number | undefined;

const loadFirstPage = async () => {
  isLoading.value = true;

  try {
    const res = await props.fetchPage({
      page: 1,
      pageSize: props.pageSize!,
      search: search.value.trim(),
    });

    items.value = res.items;
    hasMore.value = res.hasMore;
    page.value = 1;
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async () => {
  if (!hasMore.value || isLoading.value || isLoadingMore.value) return;
  isLoadingMore.value = true;
  try {
    const nextPage = page.value + 1;
    const res = await props.fetchPage({
      page: nextPage,
      pageSize: props.pageSize!,
      search: search.value.trim(),
    });
    items.value = [...items.value, ...res.items];
    hasMore.value = res.hasMore;
    page.value = nextPage;
  } finally {
    isLoadingMore.value = false;
  }
};

const onSearchUpdate = (value: string) => {
  if (internal.value && value === internal.value.name) {
    return;
  }

  search.value = value;

  if (debounceTimer) window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => {
    loadFirstPage();
  }, 300);
};

watch(menuOpen, (open) => {
  if (open && items.value.length === 0) {
    loadFirstPage();
  }
});
</script>

<template>
  <v-autocomplete
    v-model="internal"
    v-model:menu="menuOpen"
    :items="items"
    item-title="name"
    item-value="id"
    return-object
    clearable
    :disabled="disabled"
    :loading="loading || isLoading"
    :label="label"
    :placeholder="placeholder"
    variant="outlined"
    density="comfortable"
    rounded="lg"
    hide-details="auto"
    :menu-props="{ maxHeight: 360 }"
    :prepend-inner-icon="prependIcon"
    :no-filter="true"
    :close-on-content-click="false"
    @update:search="onSearchUpdate"
  >
    <template #prepend-item>
      <div v-if="isLoading" class="sa-loader">
        <v-progress-circular indeterminate size="20" width="2" />
      </div>
    </template>

    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" :title="item.raw.name" />
    </template>

    <template #append-item>
      <div v-if="hasMore" class="sa-footer" v-intersect="loadMore">
        <v-progress-circular
          v-if="isLoadingMore"
          indeterminate
          size="18"
          width="2"
        />
        <span v-else class="sa-footer-text">{{
          t("views.captureView.manual.loadMore")
        }}</span>
      </div>

      <div v-else-if="!isLoading && items.length === 0" class="sa-footer">
        <span class="sa-footer-text">
          {{ t("views.captureView.manual.noResult") }}
        </span>
      </div>
    </template>
  </v-autocomplete>
</template>

<style scoped>
.server-autocomplete .v-field {
  background: rgba(255, 255, 255, 0.85);
}

.server-autocomplete .v-field__outline {
  opacity: 0.9;
}

.sa-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px 0 14px;
}

.sa-footer-text {
  font-size: 12px;
  opacity: 0.75;
}

.sa-loader {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}
</style>
